import { z } from "zod";
import { useFormikContext } from "formik";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { promoteRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { promotionCreativeQuery } from "@/app/Promotions/GraphQL/promotionCreativeQuery";
import { AdPreviewCard } from "@/app/Promotions/Components/AdPreviewCard";
import { PlatformSelectInput } from "@/app/Promotions/Components/PlatformSelectInput";
import { ALL_PLATFORMS } from "@/app/Promotions/Components/PlatformIcon";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Currency, PromotionPlatform } from "@/gql/graphql";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

const COMING_SOON_PLATFORMS = [
  PromotionPlatform.Google,
  PromotionPlatform.Tiktok,
];

const AVAILABLE_PLATFORMS = ALL_PLATFORMS.filter(
  (platform) => !COMING_SOON_PLATFORMS.includes(platform),
);

type PromoteFormValues = {
  budget: number | null;
  platforms: PromotionPlatform[];
};

export function PromoteRelocationDialog({
  relocationId,
}: {
  relocationId: string;
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(promoteRelocationMutation);
  const { data } = useGqlQuery(promotionCreativeQuery, {
    relocationId,
  });
  const t = useTranslations("promotion.dialog");

  const validation = z.object({
    budget: z
      .number({ error: t("budgetRequired") })
      .min(1, { error: t("budgetRequired") }),
    platforms: z
      .array(z.enum(PromotionPlatform))
      .min(1, { error: t("platformsRequired") }),
  });

  return (
    <BaseDialog title={t("title")}>
      <div className="space-y-6">
        <InfoBanner>{t("description")}</InfoBanner>

        <GenericForm<PromoteFormValues>
          validationSchema={toFormikValidationSchema(validation)}
          initialValues={{
            budget: null,
            platforms: AVAILABLE_PLATFORMS,
          }}
          onSubmit={async (values) => {
            await mutateAsync({
              id: relocationId,
              budget_amount: values.budget ?? 0,
              platforms: values.platforms,
            });

            toast.success(t("success"));

            await close();
          }}
        >
          <div className="col-span-full space-y-2 lg:col-span-7">
            <p className="text-sm font-medium text-gray-900">{t("preview")}</p>
            {data ? (
              <AdPreviewCard
                className="max-w-none"
                creative={data.relocationPromotionCreative}
              />
            ) : (
              <div className="aspect-square w-full animate-pulse rounded-lg bg-gray-100" />
            )}
          </div>

          <div className="col-span-full space-y-6 lg:col-span-5">
            <PlatformSelectInput
              name="platforms"
              label={t("platformsLabel")}
              disabledPlatforms={COMING_SOON_PLATFORMS}
              disabledLabel={t("comingSoon")}
            />

            <CurrencyInput
              autoFocus
              name="budget"
              label={t("budgetLabel")}
              currency={Currency.Usd}
            />

            <BudgetSplitHint />
          </div>

          <DialogSubmitButton text={t("submit")} />
        </GenericForm>
      </div>
    </BaseDialog>
  );
}

function BudgetSplitHint() {
  const { values } = useFormikContext<PromoteFormValues>();
  const t = useTranslations("promotion.dialog");

  if (!values.budget || values.platforms.length < 2) {
    return null;
  }

  return (
    <p className="-mt-3 text-sm text-gray-500">
      {t("budgetSplit", {
        amount: formatCurrency(
          Math.floor(values.budget / values.platforms.length),
          Currency.Usd,
        ),
        count: values.platforms.length,
      })}
    </p>
  );
}
